/**
 * Performance-meting voor de cameraloop.
 * Meet effectieve FPS, frame-interval, gemiste frames (gaten in rVFC),
 * verwerkingstijd en (waar beschikbaar) heapgebruik.
 */

import type { CameraFrameStats } from "./types";

export class PerformanceTracker {
  private framesProcessed = 0;
  private missedFrames = 0;
  private lastTs: number | null = null;
  private emaIntervalMs = 33;
  private windowStart = 0;
  private windowFrames = 0;
  private lastProcessMs = 0;
  private usedJSHeapMB: number | null = null;
  private maxGapMs = 500;

  constructor(maxGapMs = 500) {
    this.maxGapMs = maxGapMs;
  }

  /** Roep aan zodra een frame wordt verwerkt. */
  frame(now: number, processMs: number): void {
    if (this.lastTs !== null) {
      const gap = now - this.lastTs;
      if (gap > this.maxGapMs) {
        // rVFC/rAF gat: minstens één frame gemist (heuristiek).
        this.missedFrames += Math.round(gap / this.maxGapMs);
      }
      this.emaIntervalMs = this.emaIntervalMs * 0.9 + gap * 0.1;
    }
    this.lastTs = now;
    this.framesProcessed += 1;
    this.windowFrames += 1;
    if (this.windowStart === 0) this.windowStart = now;
    if (now - this.windowStart > 2000) {
      this.windowStart = now;
      this.windowFrames = 0;
    }
    this.lastProcessMs = processMs;
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    if (mem && typeof mem.usedJSHeapSize === "number") {
      this.usedJSHeapMB = mem.usedJSHeapSize / (1024 * 1024);
    }
  }

  reset(): void {
    this.framesProcessed = 0;
    this.missedFrames = 0;
    this.lastTs = null;
    this.emaIntervalMs = 33;
    this.windowStart = 0;
    this.windowFrames = 0;
    this.lastProcessMs = 0;
    this.usedJSHeapMB = null;
  }

  snapshot(): CameraFrameStats {
    return {
      fps: this.emaIntervalMs > 0 ? 1000 / this.emaIntervalMs : 0,
      frameIntervalMs: this.emaIntervalMs,
      missedFrames: this.missedFrames,
      lastProcessMs: this.lastProcessMs,
      usedJSHeapMB: this.usedJSHeapMB,
      framesProcessed: this.framesProcessed,
    };
  }
}
